import React, { useContext, useState, useEffect } from 'react';

type Theme = 'light' | 'dark';

interface IContextProps {
  theme: Theme;
  toggleTheme: () => void;
  changeTheme: (theme: Theme) => void;
}

const ThemeContext = React.createContext<IContextProps | undefined>(undefined);

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }

  return context;
};

interface IProps {
  children?: React.ReactNode;
}

const ThemeProvider: React.FC<IProps> = ({ children }) => {
  const [theme, setTheme] = useState<Theme>(
    (localStorage.getItem('theme') as Theme) || 'light',
  );

  const changeTheme = (t: Theme) => {
    if (t !== theme) {
      setTheme(t);
    }
  };

  useEffect(() => {
    document.documentElement.classList.toggle('dark', theme === 'dark');
    document.documentElement.setAttribute('data-theme', theme);
    localStorage.setItem('theme', theme);
  }, [theme]);

  return (
    <ThemeContext.Provider
      value={{
        theme,
        toggleTheme: () => setTheme(theme === 'dark' ? 'light' : 'dark'),
        changeTheme,
      }}
    >
      {children}
    </ThemeContext.Provider>
  );
};

export default ThemeProvider;
